import { ANIMATION_STAGES } from "./animationConstants";
import {
  multipleTransitionPresets,
  singleTransitionPresets
} from "views/components/Editor/actionbar/buttons/animation/animationPresets";

/**
 * Converts a single cssAnimation object to cssAnimations array
 * @param {Object} cssAnimationOrElement - Either a cssAnimation object or element with cssAnimation property
 * @returns {Array} Array of animation objects for the new format
 */
export const generateCssAnimationArray = cssAnimationOrElement => {
  // Handle both direct cssAnimation object and element with cssAnimation property
  let cssAnimation;

  if (
    cssAnimationOrElement === null ||
    cssAnimationOrElement === undefined ||
    cssAnimationOrElement === ""
  ) {
    return [];
  }

  // If it's an element object with cssAnimation property
  if (
    cssAnimationOrElement &&
    typeof cssAnimationOrElement === "object" &&
    cssAnimationOrElement.cssAnimation
  ) {
    cssAnimation = cssAnimationOrElement.cssAnimation;
  } else if (
    cssAnimationOrElement &&
    typeof cssAnimationOrElement === "object" &&
    !cssAnimationOrElement.cssAnimation &&
    (cssAnimationOrElement.id ||
      cssAnimationOrElement.uniqueId ||
      cssAnimationOrElement.type)
  ) {
    // This is an element object with no cssAnimation property - return empty array
    return [];
  } else {
    // It's a direct cssAnimation object
    cssAnimation = cssAnimationOrElement;
  }

  // Return empty array if no animation provided
  if (!cssAnimation || typeof cssAnimation !== "object") {
    return [];
  }

  // If it's already an array, return as-is (already migrated)
  if (Array.isArray(cssAnimation)) {
    return cssAnimation;
  }

  const animationName = cssAnimation.name;
  const transition = cssAnimation.transition || "";

  // Determine target stage based on transition type and animation preset category
  let targetStage;

  if (transition === "in") {
    // "in" transitions always go to start stage
    targetStage = ANIMATION_STAGES.START;
  } else if (transition === "out") {
    // "out" transitions always go to end stage
    targetStage = ANIMATION_STAGES.END;
  } else {
    // No explicit transition - determine by preset category
    if (multipleTransitionPresets.includes(animationName)) {
      // Multiple transition presets can be used for start/end, default to start for neutral transitions
      targetStage = ANIMATION_STAGES.START;
    } else if (singleTransitionPresets.includes(animationName)) {
      // Single transition presets (like pulse, shake) go to middle stage
      targetStage = ANIMATION_STAGES.MIDDLE;
    } else {
      // Unknown animation type, default to start stage
      targetStage = ANIMATION_STAGES.START;
    }
  }

  // Create the new animation object
  const newAnimation = {
    stage: targetStage,
    name: cssAnimation.name || "none",
    direction: cssAnimation.direction || "",
    delay: cssAnimation.delay || 0,
    transition: cssAnimation.transition || "",
    textType: cssAnimation.textType || "",
    isPreview: cssAnimation.isPreview || false,
    duration: cssAnimation.duration || null
  };

  return [newAnimation];
};

/**
 * Migrates single cssAnimation to cssAnimations array
 * @param {Object} element - Element with potential old animation structure
 * @returns {Object} Element with migrated animation structure
 */
export const migrateSingleAnimation = element => {
  // Skip if already migrated or no animation
  if (element.cssAnimations || !element.cssAnimation) {
    return element;
  }

  const cssAnimationsArray = generateCssAnimationArray(element);

  return {
    ...element,
    cssAnimations: cssAnimationsArray,
    // Keep old property for backwards compatibility during transition
    _migratedFrom: "cssAnimation"
  };
};

/**
 * Migrates all elements in a design
 * @param {Object} designData - Design data with elements
 * @returns {Object} Design data with migrated elements
 */
export const migrateDesignAnimations = designData => {
  if (!designData || !designData.elements) {
    return designData;
  }

  const migratedElements = {};
  let migrationCount = 0;

  Object.values(designData.elements).forEach(element => {
    const migratedElement = migrateSingleAnimation(element);
    migratedElements[element.uniqueId] = migratedElement;

    if (migratedElement._migratedFrom) {
      migrationCount++;
    }
  });

  console.log(
    `Migrated ${migrationCount} elements from single to multiple animations`
  );

  return {
    ...designData,
    elements: migratedElements,
    _animationMigrationApplied: true
  };
};

/**
 * Backwards compatibility: Convert cssAnimations back to cssAnimation for legacy code
 * @param {Object} element - Element with cssAnimations array
 * @returns {Object} Element with legacy cssAnimation property
 */
export const toLegacyAnimation = element => {
  if (!element.cssAnimations || element.cssAnimations.length === 0) {
    return { ...element, cssAnimation: null };
  }

  // Use first animation or "start" stage animation as legacy
  const primaryAnimation =
    element.cssAnimations.find(anim => anim.stage === "start") ||
    element.cssAnimations[0];

  return {
    ...element,
    cssAnimation: {
      name: primaryAnimation.name,
      direction: primaryAnimation.direction,
      delay: primaryAnimation.delay,
      transition: primaryAnimation.transition,
      textType: primaryAnimation.textType,
      isPreview: primaryAnimation.isPreview
    }
  };
};
